import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Indicator } from '../../types';
import { IndicatorDialog } from './IndicatorDialog';
import { Portal } from '../Portal';
import { useRef } from 'react';

import { useOnClickOutside } from 'usehooks-ts';
import { useKeyboardEvent } from '../../hooks/useKeyboardEvent';

function AlternateActionMenu(props: {
    onClickOutside: () => void;
    items: { label: string; onClick: () => void }[];
}) {
    const ref = useRef(null);
    useOnClickOutside(ref, props.onClickOutside);

    return (
        <ul ref={ref} className="menu bg-base-200 rounded-box w-56">
            {props.items.map((item) => (
                <li key={item.label}>
                    <button
                        className="hover:bg-primary hover:text-primary-content"
                        onClick={() => {
                            props.onClickOutside();
                            item.onClick();
                        }}
                    >
                        {item.label}
                    </button>
                </li>
            ))}
        </ul>
    );
}

export function FileSelect2(props: {
    indicators: Indicator[];
    label: string;
    value: string;
    onChange: (value: string) => void;
    labelIcon?: JSX.Element;
    indicatorsMap: Record<string, Indicator>;
    tooltip: string;
    tiny?: boolean;
    alternateActions?: { label: string; onClick: () => void }[];
}) {
    const { i18n } = useTranslation();
    const [tooltipDir, setTooltipDir] = useState<'top' | 'bottom'>('top');
    const [shouldShowModal, setShouldShowModal] = useState(false);
    const [shouldShowAlternateAction, setShouldShowAlternateAction] =
        useState(false);
    const [alternateActionPos, setAlternateActionPos] = useState({
        x: 0,
        y: 0,
    });

    useKeyboardEvent('Escape', () => {
        setShouldShowModal(false)
    })

    let activeIndicator: Indicator;
    if (props.value === 'kommun') {
        console.log(props.value);
        activeIndicator = {
            id: 'kommun',
            label: { sv: 'Kommun' },
            subLabel: { sv: '' },
            show: true,
            unitFormat: 'st',
            urls: {
                deso: '',
                regso: '',
                kommun: '',
                nyko6: '',
            },
            valueKey: '',
            category: { sv: 'Administration' },
            description: { sv: '' },
        };
    } else {
        activeIndicator = props.indicatorsMap[props.value];
    }

    if (props.value === 'kommun') {
        console.log(activeIndicator);
    }
    const useAlternateAction =
        props.alternateActions && props.alternateActions.length > 0;

    const handleClick = (e: MouseEvent) => {
        if (useAlternateAction) {
            setAlternateActionPos({
                x: e.clientX,
                y: e.clientY,
            });
            setShouldShowAlternateAction(true);
        } else {
            setShouldShowModal(true);
        }
    };

    let adjustedAlternateActionsPosition = {
        x: alternateActionPos.x - 20,
        y: alternateActionPos.y - 20,
    };
    if (props.tiny) {
        adjustedAlternateActionsPosition = {
            x: alternateActionPos.x + 10,
            y: alternateActionPos.y - 20,
        };
    }

    return (
        <>
            {props.tiny && (
                <div
                    className="tooltip  tooltip-right w-full"
                    data-tip={props.label}
                >
                    <button
                        onClick={handleClick}
                        className={`btn btn-sm btn-primary grid place-items-center w-full`}
                    >
                        {props.labelIcon}
                    </button>
                </div>
            )}
            {!props.tiny && (
                <div className="grid grid-cols-[40px_1fr] gap-2">
                    {props.labelIcon && (
                        <div className="grid place-items-center select-label text-right font-bold capitalize">
                            {props.labelIcon}
                        </div>
                    )}
                    {!props.labelIcon && (
                        <div className="select-label text-right font-bold capitalize">
                            {props.label}
                        </div>
                    )}
                    <div className="relative">
                        <button
                            type="button"
                            className={`
                        text-left w-full rounded-md tooltip tooltip-${tooltipDir} tooltip-secondary
                        hover:bg-primary hover:text-primary-content hover:rounded-md hover:shadow-lg
                        px-3 py-1`}
                            data-tip={props.tooltip}
                            onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipDir(rect.top < 120 ? 'bottom' : 'top');
                            }}
                            onClick={handleClick}
                        >
                            <div className="text-xs">
                                {activeIndicator.category?.[i18n.language] ??
                                    activeIndicator.category?.['sv']}
                            </div>
                            <div className="text-base font-light">
                                {activeIndicator.label[i18n.language] ??
                                    activeIndicator.label['sv']}
                            </div>
                            <div className="text-sm font-bold">
                                {activeIndicator.subLabel[i18n.language] ??
                                    activeIndicator.subLabel['sv']}
                            </div>
                        </button>
                    </div>
                </div>
            )}

            {shouldShowAlternateAction && (
                <Portal>
                    <div
                        className="absolute"
                        style={{
                            top: `${adjustedAlternateActionsPosition.y}px`,
                            left: `${adjustedAlternateActionsPosition.x}px`,
                        }}
                    >
                        <AlternateActionMenu
                            onClickOutside={() => {
                                setShouldShowAlternateAction(false);
                            }}
                            items={[
                                {
                                    label: 'Indikator',
                                    onClick: () => {
                                        setShouldShowAlternateAction(false);
                                        setShouldShowModal(true);
                                    },
                                },
                                ...props.alternateActions!,
                            ]}
                        />
                    </div>
                </Portal>
            )}

            {shouldShowModal && (
                <Portal>
                    <div className="fixed inset-0 bg-black bg-opacity-20 isolate grid place-items-center pointer-events-none z-50">
                        <div
                            aria-hidden="true"
                            className="fixed inset-0 pointer-events-auto cursor-pointer"
                            onClick={() => {
                                setShouldShowModal(false);
                            }}
                        />
                        <IndicatorDialog
                            indicators={props.indicators}
                            value={props.value}
                            onChange={(value) => {
                                props.onChange(value);
                                setShouldShowModal(false);
                            }}
                            onClose={() => {
                                setShouldShowModal(false);
                            }}
                            tooltip={props.tooltip}
                            indicatorsMap={props.indicatorsMap}
                            activeIndicator={activeIndicator}
                        />
                    </div>
                </Portal>
            )}
        </>
    );
}
